import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { BillingView, DomainView, SiteContext, ThemeMarketView } from '@icetus/contracts';
import { ROLE_PERMISSIONS } from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import {
  ApiError,
  createDomain,
  createSite,
  archiveSite,
  fetchBilling,
  fetchThemeMarket,
  fetchDomains,
  loadMe,
  fetchSiteList,
  fetchSites,
  forgotPassword,
  getDomain,
  login,
  logout,
  removeDomain,
  resendVerification,
  resumeSite,
  verifyDomain,
} from './api';
import type { MeResponse } from './api';
import { BillingPage } from './pages/billing-page';
import { ThemeMarketPage } from './pages/theme-market-page';
import { AuditPage } from './pages/audit-page';
import { MembersPage } from './pages/members-page';
import { ProfilePage } from './pages/profile-page';
import { InviteGate } from './pages/invite-page';
import { ResetGate } from './pages/reset-page';
import { OnboardingCard } from './components/onboarding-card';
import { Modal } from './components/overlay';
import { useConfirm } from './components/confirm';
import { ProvisioningPanel } from './components/provisioning-panel';
import { CatalogPage } from './pages/catalog-page';
import { ContentPage } from './pages/content-page';
import { ShellPage } from './pages/shell-page';
import { NavPage } from './pages/nav-page';
import { TranslationPage } from './pages/translation-page';
import { MediaPage } from './pages/media-page';
import { SiteSettingsPage } from './pages/site-settings-page';
import { SiteWizard } from './pages/site-wizard';
import { SiteAgentPanel } from './pages/site-agent-panel';
import { PlatformPage } from './pages/platform-page';
import { LeadsPage } from './pages/leads-page';
import { ResourcesPage } from './pages/resources-page';
import {
  navGroupsFor,
  navFooterFor,
  navItem,
  navGroupKeyOf,
  navStaysFor,
  navSubInline,
  collapsedByDefault,
} from './lib/nav';
import type { NavGroupKey, NavKey } from './lib/nav';
import { SiteCard } from './components/site-card';
import { Brand } from './components/brand';
import { NavGroupIcon } from './components/nav-group-icon';

/**
 * 平台控制台。
 *
 * 认证从"输入共享管理员令牌"换成了"邮箱密码登录 + 服务端会话"。
 * 租户列表来自 `GET /v1/auth/me`，也就是当前用户的成员关系——
 * 不再是运维视角的全量名册。这意味着**客户看不到别人的租户名**，
 * 而且他就算手动改 `x-tenant-id`，服务端也会因为查不到 memberships 而 403。
 *
 * 域名页仍是这一版的重点：客户绑自有域名的完整闭环（建绑定 → 拿到两条 DNS
 * 记录 → 验证 → 等证书 → 解绑），每一步的失败原因都用人话显示。
 */

/**
 * 官网上的域名绑定图文步骤。
 *
 * 写死正式域名而不是从环境变量注入：控制台是纯静态产物，没有运行时配置；
 * 而这条链接指向的是**给终端客户看的帮助页**，本地开发时点过去看到生产文档
 * 也完全成立（不像 API 地址那样必须跟着环境走）。
 *
 * 指向 icetus.com 而不是 icetus.net：官网主域已收口到 .com（canonical 与
 * sitemap 都跟着走），.net 留在 Cloudflare 后面只服务客户站点，从这里点过去
 * 会绕一圈慢链路。
 */
const DNS_GUIDE_URL = 'https://icetus.com/faq/dns';

/**
 * 平台官网首页。
 *
 * 控制台上的注册入口已经撤掉：账号统一在官网开通（开通即建站），
 * 控制台只管登录。这里留一条回去的路 —— 新客户手上没有账号时，
 * 登录框是他唯一能到的页面，没有出口就成了死胡同。
 *
 * 与 DNS_GUIDE_URL 同样的理由写死正式域名：官网由 icetus.com 渲染，
 * 控制台是另一个站（console.icetus.com），拿走控制台产物单独部署时链接依然成立。
 */
const SITE_HOME_URL = 'https://icetus.com';

/**
 * 菜单结构、分组与可见性判定都在 `./lib/nav`。
 *
 * 从本文件搬出去的原因是它变成了**有分支的业务逻辑**（分组 + 按角色裁剪），
 * 留在组件里只能靠手点五个角色去验；抽成纯模块后 `nav.test.ts` 能直接断言。
 * 顶栏标题也仍然从那里取，标签只有一份真源。
 */

export default function App() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [tenantId, setTenantId] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [verifiedNotice, setVerifiedNotice] = useState<'ok' | 'invalid' | null>(null);

  /**
   * 邮箱验证是**从邮件里点进来的**，落地的这一刻浏览器里通常还没有会话，
   * 所以提示必须挂在登录页上，不能只在控制台内部显示。
   *
   * 读完立刻从地址栏抹掉参数：留在 URL 里的话，客户刷新一次就再看到一遍
   * "邮箱已验证"，第二次看到会以为验证又失败了。
   */
  useEffect(() => {
    const value = new URLSearchParams(window.location.search).get('verified');
    if (value !== 'ok' && value !== 'invalid') return;
    setVerifiedNotice(value);
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  /**
   * 邀请令牌。控制台是 SPA，nginx 的 `try_files $uri /index.html` 会把
   * `/invite/<token>` 也交给前端，所以这里自己从 pathname 里取。
   *
   * 只在首次挂载时读一次：接受成功后我们会 replaceState 把地址换成 `/`，
   * 挂载时再读就取不到令牌了，而那正是我们想要的（用过的链接不该还能用）。
   */
  const inviteToken = useMemo(() => {
    const matched = /^\/invite\/([^/?#]+)/.exec(window.location.pathname);
    return matched?.[1] ? decodeURIComponent(matched[1]) : null;
  }, []);

  /**
   * 重置密码令牌，同样从 pathname 取（nginx 的 try_files 会把 `/reset/<token>`
   * 也交给前端）。
   *
   * 用 state 而不是 useMemo：客户点"重新申请一次"时要把令牌清掉并回到登录页，
   * useMemo 不会重算，页面上会继续停在一个已经作废的令牌上。
   */
  const [resetToken, setResetToken] = useState<string | null>(() => {
    const matched = /^\/reset\/([^/?#]+)/.exec(window.location.pathname);
    return matched?.[1] ? decodeURIComponent(matched[1]) : null;
  });
  /** 从重置页点"重新申请"回来时，登录卡片直接展开成"忘记密码"表单 */
  const [wantForgot, setWantForgot] = useState(false);

  const applyMe = useCallback((next: MeResponse) => {
    setMe(next);
    setAuthError(null);
    setTenantId((prev) => {
      /**
       * 校验旧值是否还在名册里，而不是简单的 `prev || 第一个`。
       *
       * 换账号后旧租户 ID 仍然非空，直接沿用会让页面显示 A 的租户名、
       * 实际用 B 的租户 ID 请求——而且受控 select 的 value 不在选项里时
       * 浏览器照样显示第一项，看起来完全正常。这个坑踩过一次。
       */
      if (prev && next.memberships.some((m) => m.tenantId === prev)) return prev;
      return next.memberships[0]?.tenantId ?? '';
    });
  }, []);

  // 首次加载先问"我是谁"。cookie 有效就直接进控制台，否则显示登录页。
  useEffect(() => {
    let cancelled = false;
    loadMe()
      .then((next) => {
        if (!cancelled) applyMe(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 401 就是"没登录"，属于正常状态，不该显示成错误
        if (err instanceof ApiError && err.status === 401) {
          setMe(null);
          setAuthError(null);
          return;
        }
        setAuthError(err instanceof ApiError ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applyMe]);

  if (bootstrapping) return <div className="boot">正在校验登录状态…</div>;

  /**
   * 登录成功后直接把响应喂给 `applyMe`，不再补一次 `loadMe()`。
   *
   * 之所以可以这样：`login()` 内部已经过了 `withPlatformTenants`，
   * 和刷新时 `loadMe()` 的产物一致。这里**曾经**直接用登录响应当名册，
   * 而登录响应里的 memberships 是真实成员关系 —— 超管不属于任何租户，
   * 于是登录进去租户是空的、刷新一下才好。类型都是 MeResponse，
   * 编译器看不出这个差别，所以补齐放在 api 层而不是这里。
   */
  const submitLogin = async (email: string, password: string): Promise<void> => {
    applyMe(await login(email, password));
  };

  /**
   * 重置密码优先于登录门禁，也优先于邀请。
   *
   * 客户是从邮件里点进来的，那一刻他多半没有会话；走登录门禁只会看到一个
   * 登录框，而他恰恰是登不进去才来重置的。令牌无效时留在这一页给出明确结果，
   * 而不是把 URL 覆盖掉再假装什么都没发生。
   */
  if (resetToken) {
    return (
      <ResetGate
        token={resetToken}
        onBackToLogin={() => {
          setResetToken(null);
          setWantForgot(true);
          window.history.replaceState({}, '', '/');
        }}
      />
    );
  }

  /**
   * 邀请链接优先于一切。
   *
   * 放在登录门禁**之前**：被邀请人点进来时通常没有会话，走登录门禁只会
   * 看到一个普通登录页 —— 他会以为链接坏了。这里先读邀请（公开接口，
   * 不需要登录），把"谁邀请你、什么角色、必须用哪个邮箱"讲清楚，再把
   * 登录框摆在下面。
   */
  if (inviteToken) {
    return (
      <InviteGate
        token={inviteToken}
        me={me}
        renderLogin={(hint) => (
          <LoginGate
            error={authError}
            verifiedNotice={verifiedNotice}
            hint={hint}
            onSubmit={submitLogin}
          />
        )}
        onSignOut={async () => {
          try {
            await logout();
          } finally {
            setMe(null);
            setTenantId('');
          }
        }}
        onJoined={async () => {
          applyMe(await loadMe());
        }}
      />
    );
  }

  if (!me) {
    return (
      <LoginGate
        error={authError}
        verifiedNotice={verifiedNotice}
        initialMode={wantForgot ? 'forgot' : 'login'}
        onSubmit={submitLogin}
      />
    );
  }

  return (
    <Shell
      me={me}
      tenantId={tenantId}
      onTenantChange={setTenantId}
      onRefreshMe={async () => {
        // 开通跑完后租户名册会变，而名册只在登录时取过一次。
        // 不重取的话客户会看到"站点已就绪"却仍然进不去正文 —— 因为 tenantId 是空的
        applyMe(await loadMe());
      }}
      onSignOut={async () => {
        try {
          await logout();
        } finally {
          // 即使服务端登出失败也要清本地状态：否则用户会卡在"点了没反应"
          setMe(null);
          setTenantId('');
        }
      }}
    />
  );
}

function LoginGate({
  error,
  verifiedNotice,
  hint,
  initialMode,
  onSubmit,
}: {
  error: string | null;
  verifiedNotice: 'ok' | 'invalid' | null;
  /** 邀请落地等场景需要在登录框上方先讲一句话。为它加一个槽位，
   *  好过为此再抄一份登录表单出来 */
  hint?: ReactNode;
  /** 从重置页点"重新申请"回来时直接展开找回密码表单 */
  initialMode?: 'login' | 'forgot';
  onSubmit: (email: string, password: string) => Promise<void>;
}) {
  const [mode, setMode] = useState<'login' | 'forgot'>(initialMode ?? 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  /**
   * 找回密码的"已提交"卡片。
   *
   * 控制台原先也能注册，这个 state 同时服务两种提交（注册说验证信、找回说重置信）；
   * 注册入口撤掉之后只剩找回一条，于是从 `{mode,email,ttl}` 收成 `{email,ttl}` ——
   * 留着一个永远等于 'forgot' 的字段，只会让下一个人以为这里还有别的分支。
   */
  const [sent, setSent] = useState<{ email: string; ttl: number } | null>(null);

  const submit = async (): Promise<void> => {
    if (busy) return;
    if (!email.trim() || (mode !== 'forgot' && !password)) {
      setLocalError(mode === 'forgot' ? '请填写邮箱' : '请填写邮箱与密码');
      return;
    }
    setBusy(true);
    setLocalError(null);
    try {
      if (mode === 'forgot') {
        const result = await forgotPassword(email.trim());
        setSent({ email: email.trim(), ttl: result.ttlMinutes });
      } else {
        await onSubmit(email.trim(), password);
      }
    } catch (err) {
      setLocalError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const shown = localError ?? error;

  if (sent) {
    return (
      <div className="login">
        <div className="login__card">
          <Brand />
          <h2 className="login__title">重置邮件已请求</h2>
          {/*
            文案必须是"如果这个邮箱注册过"而不是"已发送到你的邮箱"：
            服务端为了防止账号枚举，对未注册的邮箱返回同样的结果且不发信。
            写成肯定句，客户拿着一个打错的邮箱会一直等一封不会来的信。
          */}
          <p className="muted">
            如果 <strong>{sent.email}</strong> 是已注册的账号，我们会寄一封重置邮件，
            点开链接即可设置新密码（{sent.ttl} 分钟内有效，只能用一次）。
          </p>
          <p className="muted">
            没收到的话先看垃圾箱。仍然没有，联系我们，我们可以直接把链接发给你。
          </p>
          <Button
            variant="primary"
            onClick={() => {
              setSent(null);
              setMode('login');
              setPassword('');
            }}
          >
            返回登录
          </Button>
        </div>
        <SiteHomeLink />
      </div>
    );
  }

  return (
    <div className="login">
      <div className="login__card">
        <Brand />
        {hint ?? (
          <p className="muted">
            {mode === 'forgot'
              ? '输入注册时用的邮箱，我们会寄一封设置新密码的邮件。'
              : '登录后管理你的站点与域名。'}
          </p>
        )}

        {verifiedNotice === 'ok' ? (
          <p className="notice notice--ok">邮箱已验证，现在可以登录了。</p>
        ) : verifiedNotice === 'invalid' ? (
          <p className="notice notice--warn">
            这个验证链接无效或已过期。登录后可以在顶部重新发送验证邮件。
          </p>
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <input
            className="field"
            type="email"
            autoFocus
            autoComplete="username"
            placeholder="邮箱"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {mode !== 'forgot' ? (
            <input
              className="field"
              type="password"
              autoComplete="current-password"
              placeholder="密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          ) : null}
          <Button variant="primary" type="submit" disabled={busy}>
            {busy
              ? mode === 'forgot'
                ? '发送中…'
                : '登录中…'
              : mode === 'forgot'
                ? '发送重置邮件'
                : '登录'}
          </Button>
        </form>
        {shown ? <p className="error">{shown}</p> : null}

        {mode === 'forgot' ? (
          <button
            type="button"
            className="login__switch"
            onClick={() => {
              setMode('login');
              setLocalError(null);
            }}
          >
            返回登录
          </button>
        ) : (
          <button
            type="button"
            className="login__switch"
            onClick={() => {
              setMode('forgot');
              setPassword('');
              setLocalError(null);
            }}
          >
            忘记密码？
          </button>
        )}
      </div>
      <SiteHomeLink />
    </div>
  );
}

/**
 * 回官网首页的出口。
 *
 * 控制台**不再提供注册**：能在这里登录的账号，都是官网开通站点时建的那个。
 * 原先那颗「还没有账号？注册」按钮会让人误以为"注册"是在给已有站点加一个管理员 ——
 * 于是有人在控制台里建号，建完发现登录进来还是自己的空名册。
 * 入口直接撤掉，改成一条明确的回去的路：账号在官网开，控制台只管登录。
 *
 * 放在卡片**外面**而不是卡片里的第三颗按钮：卡片里那两颗（登录 / 忘记密码）
 * 都作用在"这一次登录"上，这一条是离开，不该混在同一组里。
 */
function SiteHomeLink() {
  return (
    <a className="login__home" href={SITE_HOME_URL}>
      返回 iCetus 官网
    </a>
  );
}

function Shell({
  me,
  tenantId,
  onTenantChange,
  onRefreshMe,
  onSignOut,
}: {
  me: MeResponse;
  tenantId: string;
  onTenantChange: (id: string) => void;
  onRefreshMe: () => Promise<void>;
  onSignOut: () => void;
}) {
  const [active, setActive] = useState<NavKey>(() => {
    /**
     * 超管默认落在「平台总览」。
     *
     * 他本来就不属于任何租户（`memberships` 里只有拼出来的超管视角），
     * 落在「我的站点」等于先让他看一个随机客户的站，再自己点走。
     * 判据用"有没有**真实**成员关系"：超管偶尔也可能是某客户的成员，
     * 那时他先看到的应该是那个客户。
     */
    const realMembership = me.memberships.some((m) => !m.viaPlatformAdmin);
    return me.user?.platformAdmin && !realMembership ? 'platform' : 'sites';
  });
  const tenant = useMemo(() => me.memberships.find((m) => m.tenantId === tenantId), [me, tenantId]);

  /** 平台总览的可见性。运维令牌背后没有账号，但接口认它，所以一起放行 */
  const canPlatform = me.user?.platformAdmin === true || Boolean(me.adminToken);

  /**
   * 建站权限从 `ROLE_PERMISSIONS` 算，不在这里写 `role === 'owner' || role === 'admin'`。
   *
   * 两处写死必然会漂移，而漂移的症状很难看：按钮在、点下去 403，
   * 或者反过来 —— 有权限的人根本看不到入口。权限矩阵只有一个真源，
   * 前端只是把它翻译成"显示还是隐藏"，最终仍由服务端判。
   */
  const canCreateSite = tenant ? ROLE_PERMISSIONS[tenant.role].includes('site:create') : false;
  /**
   * 能不能归档/恢复站点。与建站分开：建站吃名额、产生新地址；
   * 归档只是改一个状态，editor 也该能整理自己的站点。
   */
  const canUpdateSite = tenant ? ROLE_PERMISSIONS[tenant.role].includes('site:update') : false;

  /**
   * 能不能发起翻译。
   *
   * `translation:run` 只给 admin / owner —— 一次全站翻译会在我们的账单上
   * 留下真实的一笔，编辑不该有这个按钮（服务端也会拦，但让他点了才知道
   * 自己被拒更糟）。
   */
  const canRunTranslation = tenant
    ? ROLE_PERMISSIONS[tenant.role].includes('translation:run')
    : false;

  /**
   * 当前租户有没有站点。
   *
   * 侧栏靠它决定"零站点时只留「我的站点」"。三态而不是布尔：
   * `undefined` = 还没问出来（站点列表是异步拉的），此时**按全量渲染**。
   * 一进页面就按"没站点"渲染的话，侧栏会先只剩一项、半秒后再全部出现，
   * 看着像坏了。
   *
   * 数据由 SitesPage 上报（它本来就要拉这个列表），不在这里重复请求一次。
   */
  const [hasSite, setHasSite] = useState<boolean | undefined>(undefined);

  /**
   * 建站向导里有没有没提交的草稿。
   *
   * 状态在向导里、拦住"离开"的动作却在外壳（点侧栏那一瞬），
   * 所以由向导上报、这里判。不接这一条的话，客户填了名字选了版式、
   * 顺手点一下「页面内容」，回来就什么都不剩，而这中间**没有任何提示**。
   */
  const [wizardDirty, setWizardDirty] = useState(false);

  // 切租户时回到"未知"，等 SitesPage 拉到数据再落定 —— 否则会拿
  // 上一个租户的结论去裁新租户的侧栏
  useEffect(() => {
    setHasSite(undefined);
    // 草稿是挂在租户下的，切走就等于作废，脏值不能跟着带到新租户
    setWizardDirty(false);
  }, [tenantId]);

  /**
   * 侧栏菜单：分好组、并按当前角色裁掉看不见的项。
   *
   * 判定逻辑在 `lib/nav` 里（纯函数，有测试）。这里只负责把上下文递进去。
   * `tenant` 可能是 undefined —— 还没建站的客户、以及拿运维令牌访问的超管，
   * 那种情况下 `navGroupsFor` 会**不做权限过滤**，把入口全留着，
   * 因为点进去看到的是开通引导，而不是 403。
   */
  const navGroups = useMemo(
    () => navGroupsFor({ role: tenant?.role, canPlatform, hasSite }),
    [tenant?.role, canPlatform, hasSite],
  );

  /** 底栏按钮（个人资料）。和分组分开取：它不参与折叠，也不在 <nav> 里 */
  const navFooter = useMemo(
    () => navFooterFor({ role: tenant?.role, canPlatform, hasSite }),
    [tenant?.role, canPlatform, hasSite],
  );

  /**
   * 当前页被裁掉了怎么办：落回「我的站点」。
   *
   * 真实场景是**切租户**——同一个人在建站客户里是 owner、在另一个客户里是 viewer，
   * 切过去之后原来停留的「套餐与账单」已经不在他的侧栏里了。
   * 不处理的话页面还渲染着账单内容、侧栏却找不出一个高亮项，
   * 客户会以为控制台卡住了。
   */
  useEffect(() => {
    /**
     * 判定放 `lib/nav`：它要同时认"分组里的项、底栏项、以及只从别处进入的
     * 隐藏项"三种。写成内联的三行 `some()` 时最容易漏掉第三种 ——
     * 客户从「站点信息 → 更多主题」进市场页，会被立刻弹回「我的站点」。
     */
    if (!navStaysFor(active, { role: tenant?.role, canPlatform, hasSite })) setActive('sites');
  }, [active, tenant?.role, canPlatform, hasSite]);

  /**
   * 折叠状态：存的是**收起了哪几组**，初始全部收起、当前页所在组除外。
   *
   * 反过来存"展开了哪几组"也行，但那样"默认折叠"要靠一个空集合表达，
   * 而新增一个分组时它会静默地默认展开 —— 存收起集合的话，新分组天然是收着的。
   *
   * 初始值里就把当前组摘掉，而不是靠下面那个 effect 首次执行去补：
   * 那样会先画一帧"全部收起"，再弹开当前组，看起来像闪了一下。
   */
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<NavGroupKey>>(() => {
    const initially = new Set(collapsedByDefault());
    const group = navGroupKeyOf(active);
    if (group) initially.delete(group);
    return initially;
  });

  /** 当前页属于哪一组。它决定了下面那个"自动展开"；底栏项是 null */
  const activeGroup = navGroupKeyOf(active);

  /**
   * 当前页所在组必须展开。
   *
   * 不加这一段会出现一个很难受的状态：刷新后所有组都收着，
   * 顶栏写着「多语言翻译」，而侧栏里连它在哪一组都看不出来。
   *
   * 依赖只有 `activeGroup`：客户**主动**收起当前所在的组时这里不会重跑，
   * 否则他点一下箭头、界面立刻弹回去，会以为坏了。
   */
  useEffect(() => {
    if (!activeGroup) return;
    setCollapsedGroups((prev) => {
      if (!prev.has(activeGroup)) return prev;
      const next = new Set(prev);
      next.delete(activeGroup);
      return next;
    });
  }, [activeGroup]);

  const toggleGroup = (key: NavGroupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /**
   * 内容页的未保存改动，由 ContentPage 上报。
   *
   * 切页面、切站点、刷新这三处 ContentPage 自己能拦；但**点侧栏去别的模块**
   * 是外壳的动作，它管不着。不拦的话，客户改完文案点「商品」去看一眼价格，
   * 回来那一页就是空的 —— 而且没有任何提示，因为他确实没点过"放弃"。
   */
  const [contentDirty, setContentDirty] = useState(false);
  /**
   * 菜单页的未保存改动。
   *
   * 与内容页分开存：两个页面互不相关，合成一个布尔值会让"在页面内容里改了
   * 一段、去菜单页改一项、再回来"这种来回跳的过程里，提示出现在错误的那一次
   * 离开上（客户会以为自己丢的是另一个页面）。
   */
  const [navDirty, setNavDirty] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const leaveContent = useCallback(
    async (to: string): Promise<boolean> => {
      if (to === active) return true;
      const dirtyLabel =
        active === 'content' && contentDirty
          ? '「内容」里还有没保存的改动（包括刚添加的区块）'
          : active === 'nav' && navDirty
            ? '菜单里还有没保存的改动'
            : /**
               * 建站向导的草稿。
               *
               * 它连草稿都没存 —— 名字、版式、Logo 全在内存里，离开就真的没了
               * （Logo 那个 File 对象也一起释放）。所以提示语气与"未保存"
               * 是同一档：这不是"回头能找回"。
               */
              active === 'sites' && wizardDirty
              ? '建站向导里填的内容（站名、版式、Logo）'
              : null;
      if (!dirtyLabel) return true;
      return confirm({
        title: '放弃未保存的修改？',
        message: `${dirtyLabel}，离开这个页面后它们会丢失。`,
        confirmText: '丢弃并离开',
        cancelText: '留在本页',
        tone: 'danger',
      });
    },
    [active, contentDirty, navDirty, wizardDirty, confirm],
  );

  const go = async (key: NavKey) => {
    if (!(await leaveContent(key))) return;
    setActive(key);
  };

  const changeTenant = async (id: string) => {
    // 切租户等于把整页数据换掉，未保存的内容一样会没
    if (id !== tenantId && !(await leaveContent(''))) return;
    onTenantChange(id);
  };

  /** 重发验证邮件。只记"发过了"，不假装邮箱已变成已验证 —— 那要等客户点链接 */
  const [resend, setResend] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [resendHint, setResendHint] = useState('');

  /**
   * 到期横幅。
   *
   * 客户平时不进「套餐与账单」页 —— 他要看到"还有 3 天到期"或"站点已停"
   * 只能是在他正在改内容的时候。所以这里单独拉一次账单，只为了顶部这一条。
   * 拉失败就当作没有横幅：账单接口挂了不该让整个控制台弹红。
   */
  const [billing, setBilling] = useState<BillingView | null>(null);
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    fetchBilling(tenantId)
      .then((next) => {
        if (!cancelled) setBilling(next);
      })
      .catch(() => {
        // 静默：横幅是提醒，不是功能
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  /**
   * 主题订阅的到期提醒。
   *
   * 与套餐那一条分开：套餐到期会让站点 404，主题到期只是"换一套皮"——
   * 但客户**不会**知道这件事，他只会某天打开站点发现"我的站变样了"。
   * 所以提前说，而且说清"到期后是回到哪套"。
   */
  const [themeMarket, setThemeMarket] = useState<ThemeMarketView | null>(null);
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    fetchThemeMarket(tenantId)
      .then((next) => {
        if (!cancelled) setThemeMarket(next);
      })
      .catch(() => {
        // 静默：提醒拉不到不该让控制台弹红
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const billingNotice = useMemo(() => {
    if (!billing) return null;
    const sub = billing.subscription;
    if (!sub.siteVisible) {
      return {
        tone: 'danger' as const,
        text: '站点已暂停：套餐已到期，续费后立刻恢复（数据保留 30 天）。',
      };
    }
    if (sub.status === 'past_due') {
      return { tone: 'warn' as const, text: '上次扣款未成功，站点仍在宽限期内，请尽快续费。' };
    }
    if (sub.daysLeft !== null && sub.daysLeft <= 3) {
      return {
        tone: 'warn' as const,
        text: `${sub.planName}还有 ${sub.daysLeft} 天到期，到期后站点会暂停。`,
      };
    }
    return null;
  }, [billing]);

  const themeNotice = useMemo(() => {
    const exp = themeMarket?.nextExpiry;
    if (!exp) return null;
    const item = themeMarket?.items.find((i) => i.name === exp.themeName);
    const cancelling = item?.subscription?.cancelAtPeriodEnd === true;
    if (cancelling) {
      return {
        tone: 'warn' as const,
        text: `「${exp.labelZh}」已提交退订，${exp.currentPeriodEnd.slice(0, 10)} 到期后站点会回到基础主题（内容不受影响）。`,
      };
    }
    if (exp.daysLeft <= 3) {
      return {
        tone: 'warn' as const,
        text: `主题「${exp.labelZh}」还有 ${Math.max(exp.daysLeft, 0)} 天到期，到期后站点会回到基础主题。续订在「站点信息 · 外观 → 更多主题」。`,
      };
    }
    return null;
  }, [themeMarket]);

  const onResend = async (): Promise<void> => {
    setResend('sending');
    try {
      const result = await resendVerification();
      if (result.alreadyVerified) {
        // 服务端说已验证：页面上的状态是旧的，刷新一次拿最新身份
        setResend('sent');
        setResendHint('这个邮箱已经验证过了，刷新一下页面即可。');
        return;
      }
      setResend('sent');
      setResendHint('已重新发送，请查收（先看垃圾箱）。');
    } catch (err) {
      setResend('failed');
      setResendHint(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div className="shell">
      <aside className="sidebar">
        <Brand />
        <nav className="nav">
          {/* 分组默认收起，组标题是 <button aria-expanded>：
              它真的能点，所以必须是按钮而不是 <p> —— 键盘用户要能用，
              读屏也要能读出"已展开 / 已折叠"。
              没有标题的那一组（平台总览，单项）不参与折叠。 */}
          {navGroups.map((group) => {
            const bare = !group.label;
            const open = bare || !collapsedGroups.has(group.key);
            return (
              <div
                key={group.key}
                className={`navgroup${bare ? ' navgroup--bare' : ''}${open ? ' is-open' : ''}`}
                role="group"
                aria-label={group.label ?? undefined}
              >
                {bare ? null : (
                  <button
                    type="button"
                    className="navgroup__title"
                    aria-expanded={open}
                    aria-controls={`navgroup-${group.key}`}
                    onClick={() => toggleGroup(group.key)}
                  >
                    <span className="navgroup__icon">
                      <NavGroupIcon name={group.key} />
                    </span>
                    {/* 标签自己占满剩余宽度，把三角顶到右端。
                        不用 justify-content: space-between —— 那样图标和文字
                        之间的 gap 也会被拉伸，两者会离得很远。 */}
                    <span className="navgroup__label">{group.label}</span>
                    {/* 三角形是纯装饰，方向由 CSS 跟着 .is-open 转；
                        aria-hidden 免得读屏念出"黑三角" */}
                    <span className="navgroup__caret" aria-hidden="true" />
                  </button>
                )}
                <div className="navgroup__items" id={`navgroup-${group.key}`} hidden={!open}>
                  {group.items.map((item) => (
                    <button
                      key={item.key}
                      className={`nav__item${active === item.key ? ' is-active' : ''}`}
                      onClick={() => void go(item.key)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        {/*
          侧栏底部：个人资料 + 退出登录。

          个人资料原来在「账号」组里，跟套餐、成员、操作记录排在一起 ——
          那三项都跟租户走，而它改的是"我这个人的名字和密码"，跟当前站点
          没关系。客户找改密码要先把账号组展开、再往下扫三行。

          收进底部后两个按钮都是"关于我"的动作，位置固定、不用展开任何组。
          个人资料用中性描边、退出登录保留危险色悬停：并排时也要看得出
          哪个是离开。
        */}
        <div className="sidebar__foot">
          {navFooter.map((item) => {
            const current = active === item.key;
            return (
              <button
                key={item.key}
                type="button"
                className={`sidebar__account${current ? ' is-active' : ''}`}
                onClick={() => void go(item.key)}
                aria-current={current ? 'page' : undefined}
              >
                {/* 人形图标：底部两个按钮形制一致，靠图标区分"我自己"和"离开" */}
                <svg
                  viewBox="0 0 16 16"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="8" cy="5.4" r="2.6" />
                  <path d="M2.9 13.4c0-2.5 2.3-4.3 5.1-4.3s5.1 1.8 5.1 4.3" />
                </svg>
                {item.label}
              </button>
            );
          })}
          {/* 退出登录独立成按钮：不再借用 .nav__item 的外观，
              否则跟导航项长得一样，客户找不到它 */}
          <button type="button" className="sidebar__signout" onClick={onSignOut}>
            {/* 图标是"门 + 向外的箭头"。纯文字在侧栏里和导航项太像，
                加一个形制不同的图标，客户扫一眼就知道这是离开，不是又一个页面 */}
            <svg
              viewBox="0 0 16 16"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M6 14H3.5A1.5 1.5 0 0 1 2 12.5v-9A1.5 1.5 0 0 1 3.5 2H6" />
              <path d="M10.5 11.5 14 8l-3.5-3.5" />
              <path d="M14 8H6" />
            </svg>
            退出登录
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div className={navSubInline(active) ? 'topbar__head is-inline' : 'topbar__head'}>
            <h1 className="topbar__title">{navItem(active)?.label}</h1>
            {/* 副标题是可选的一句话。不是每一页都需要 —— 有的页面标题已经
                说完了，再补一句"这里能做什么"只是把眼睛又拉回来读一遍。
                包在条件里而不是留一个空 <p>：空段落在顶栏底部撑出半个行高的
                空白，看起来像内容没加载出来。 */}
            {navItem(active)?.sub ? <p className="topbar__sub">{navItem(active)?.sub}</p> : null}
          </div>
          <div className="topbar__right">
            <label className="picker">
              <span>租户</span>
              <select
                className="field"
                value={tenantId}
                onChange={(e) => void changeTenant(e.target.value)}
              >
                {me.memberships.map((m) => (
                  <option key={m.tenantId} value={m.tenantId}>
                    {m.tenantName}（{m.tenantSlug}）
                  </option>
                ))}
              </select>
            </label>
            {/**
             * 超管视角要标出来，不能只显示角色名。
             *
             * 他自己没有这个租户里的角色（`role` 是拼出来的 owner），
             * 显示成 "owner" 会让人以为自己是这个客户的人 —— 而接下来
             * 他做的每件事都会记进客户的审计日志。
             */}
            {tenant ? (
              tenant.viaPlatformAdmin ? (
                <Badge tone="warn">超管视角</Badge>
              ) : (
                <Badge tone="neutral">{tenant.role}</Badge>
              )
            ) : null}
            <Badge tone="success">已连接</Badge>
          </div>
        </header>

        {billingNotice ? (
          <div className={`notice notice--${billingNotice.tone}`}>{billingNotice.text}</div>
        ) : null}
        {themeNotice ? (
          <div className={`notice notice--${themeNotice.tone}`}>{themeNotice.text}</div>
        ) : null}

        {me.user && !me.user.emailVerified ? (
          <div className="notice notice--warn">
            <span>
              邮箱 <strong>{me.user.email}</strong> 还没验证。验证前可以编辑与预览， 但
              <strong>不能发布页面或绑定域名</strong>。{resendHint ? ` ${resendHint}` : ''}
            </span>
            <Button
              variant="ghost"
              disabled={resend === 'sending' || resend === 'sent'}
              onClick={() => void onResend()}
            >
              {resend === 'sending' ? '发送中…' : resend === 'sent' ? '已发送' : '重发验证邮件'}
            </Button>
          </div>
        ) : null}

        {/**
         * 个人资料必须在"有没有租户"之前判断。
         *
         * 它不依赖 `tenantId`：客户刚注册还没建站时，也不该连自己叫什么都改不了。
         * 放进下面的三元里的话，无租户的用户点这一项会落到开通面板上。
         */}
        {active === 'profile' ? (
          me.user ? (
            <ProfilePage
              user={me.user}
              onUpdated={() => void onRefreshMe()}
              onSignOut={onSignOut}
            />
          ) : (
            <p className="muted">运维令牌背后没有具体账号，因此没有个人资料可改。</p>
          )
        ) : active === 'platform' ? (
          /**
           * 平台总览同样要排在"有没有租户"之前：它跨租户，不需要 `tenantId`。
           * 放进下面那个三元里的话，超管在没有可操作租户时（名册读取失败）
           * 点这一项会落到开通面板上。
           */
          canPlatform ? (
            <PlatformPage />
          ) : (
            <p className="muted">这份报表只有平台超管能看。</p>
          )
        ) : me.adminToken ? (
          <p className="muted">
            当前以平台运维令牌访问，没有具体用户身份。租户列表请用令牌直接调{' '}
            <code>/v1/tenants</code>。
          </p>
        ) : tenantId ? (
          active === 'sites' ? (
            <SitesPage
              tenantId={tenantId}
              emailVerified={me.user?.emailVerified ?? false}
              canCreate={canCreateSite}
              canArchive={canUpdateSite}
              onManageDomains={() => setActive('domains')}
              onNavigate={setActive}
              onSitesChanged={setHasSite}
              onWizardDirtyChange={setWizardDirty}
            />
          ) : active === 'domains' ? (
            <DomainsPage tenantId={tenantId} tenantLabel={tenant?.tenantName ?? ''} />
          ) : active === 'catalog' ? (
            // AI 出图按张计费，权限与"发起翻译"同一档（admin/owner）：editor 能改内容，
            // 但不该能开一个花钱的任务。服务端也认 `site:update`，这里只是先把入口藏起来
            <CatalogPage tenantId={tenantId} canGenerateArt={canUpdateSite} />
          ) : active === 'media' ? (
            <MediaPage tenantId={tenantId} />
          ) : active === 'resources' ? (
            <ResourcesPage tenantId={tenantId} />
          ) : active === 'leads' ? (
            <LeadsPage tenantId={tenantId} />
          ) : active === 'content' ? (
            // 与「商品分类」同一档：区块里的图也能 AI 生成，按张计费，
            // editor 看不到那个按钮（服务端也只认 site:update）
            <ContentPage
              tenantId={tenantId}
              canGenerateArt={canUpdateSite}
              onDirtyChange={setContentDirty}
            />
          ) : active === 'shell' ? (
            <ShellPage
              tenantId={tenantId}
              canRunTranslation={canRunTranslation}
              canEditSettings={canUpdateSite}
            />
          ) : active === 'nav' ? (
            <NavPage
              tenantId={tenantId}
              onDirtyChange={setNavDirty}
              onGoShell={() => setActive('shell')}
              onGoSettings={() => setActive('settings')}
            />
          ) : active === 'translation' ? (
            <TranslationPage tenantId={tenantId} canRun={canRunTranslation} />
          ) : active === 'settings' ? (
            <SiteSettingsPage
              tenantId={tenantId}
              onGoMarket={() => setActive('themes')}
              onGoTranslation={() => setActive('translation')}
              onGoShell={() => setActive('shell')}
            />
          ) : active === 'themes' ? (
            <ThemeMarketPage
              tenantId={tenantId}
              platformAdmin={me.user?.platformAdmin === true}
              onBack={() => setActive('settings')}
            />
          ) : active === 'billing' ? (
            <BillingPage tenantId={tenantId} />
          ) : active === 'members' ? (
            <MembersPage tenantId={tenantId} />
          ) : active === 'audit' ? (
            <AuditPage tenantId={tenantId} />
          ) : (
            /**
             * 兜底回到「我的站点」。原来最后一条分支直接写死 BillingPage，
             * 于是新增页面一旦漏写就会**悄悄落到账单页** —— 看着像跳转错了，
             * 实际是这里少了一个分支。现在兜底改成最无害的首页。
             */
            <SitesPage
              tenantId={tenantId}
              emailVerified={me.user?.emailVerified ?? false}
              canCreate={canCreateSite}
              canArchive={canUpdateSite}
              onManageDomains={() => setActive('domains')}
              onNavigate={setActive}
              onSitesChanged={setHasSite}
              onWizardDirtyChange={setWizardDirty}
            />
          )
        ) : (
          /**
           * 没有租户时不写"请联系平台管理员"了 —— 自助开通之后，绝大多数
           * 走到这里的人只是还差最后一两秒（或者开通失败过一次）。
           * 让他等、让他重试，而不是让他去发一封邮件。
           */
          <ProvisioningPanel onReady={() => void onRefreshMe()} />
        )}
      </main>
      {confirmDialog}
    </div>
  );
}

function SitesPage({
  tenantId,
  emailVerified,
  canCreate,
  canArchive,
  onManageDomains,
  onNavigate,
  onSitesChanged,
  onWizardDirtyChange,
}: {
  tenantId: string;
  emailVerified: boolean;
  canCreate: boolean;
  /** 没有 `site:update` 就不显示归档按钮 —— 点了也是 403 */
  canArchive: boolean;
  onManageDomains: () => void;
  onNavigate: (key: 'content' | 'catalog' | 'settings' | 'domains') => void;
  /**
   * 站点从"没有"变成"有"（或反过来）时上报一次。
   *
   * 侧栏靠它决定"零站点时只留「我的站点」"。数据在这里本来就要拉，
   * 让上层再请求一遍同一个接口只是多一次往返。
   */
  onSitesChanged?: (hasSite: boolean) => void;
  /** 向导草稿脏值上报（外壳据此在离开时拦一下） */
  onWizardDirtyChange?: (dirty: boolean) => void;
}) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  /**
   * 站点用量。`limit > 0` 才代表有上限，`0` 是"不限量"（套餐里没配这一项）。
   * 初值给 0/0 而不是 0/1：拿到真数据之前不要把"新增站点"按钮画成禁用态 ——
   * 一闪而过的禁用按钮比晚 200ms 出现更难解释。
   */
  const [usage, setUsage] = useState<{ used: number; limit: number }>({ used: 0, limit: 0 });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [draftName, setDraftName] = useState('');
  /**
   * 向导开着没有。
   *
   * 放在这一页里而不是外壳，是因为**入口就在这一页的空态上**，
   * 而且它不需要一个 NavKey —— 控制台没有路由系统，为一个只在
   * 零站点时出现一次的流程去动侧栏表，只会让 `navStaysFor` 多一条
   * 要维护的例外。向导退出就是回到「我的站点」。
   */
  const [wizardOpen, setWizardOpen] = useState(false);
  /**
   * AI 建站开着没有。
   *
   * 和向导并列，理由也一样：入口在零站点空态上，它不需要一个 NavKey。
   * 两者的差别只是"谁来写内容"—— 向导让客户自己挑版式填名字，
   * AI 让客户说四个词、由系统把页面文案和配图一起做出来。
   */
  const [agentOpen, setAgentOpen] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    const data = await fetchSiteList(tenantId);
    setSites(data.items);
    setUsage(data.usage);
    onSitesChanged?.(data.items.length > 0);
  }, [tenantId, onSitesChanged]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSiteList(tenantId)
      .then((data) => {
        if (!cancelled) {
          setSites(data.items);
          setUsage(data.usage);
          setError(null);
          // 零站点 → 侧栏收窄到只剩「我的站点」。放在这里而不是另开一个
          // 请求：这份数据本来就是这一页的主数据
          onSitesChanged?.(data.items.length > 0);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, onSitesChanged]);

  const atLimit = usage.limit > 0 && usage.used >= usage.limit;
  /**
   * 同时在线数。恢复暂停的站点受它限制，而不是受"已用名额"限制 ——
   * 暂停的站也算在名额里，拿名额来判断的话，从企业版降下来的客户
   * 永远恢复不了任何一个。
   */
  const liveCount = sites.filter((s) => s.site.status === 'live').length;
  const resumeBlocked = usage.limit > 0 && liveCount >= usage.limit;

  const onCreate = async (): Promise<void> => {
    const name = draftName.trim();
    if (!name) return;
    setBusy('create');
    setError(null);
    try {
      const created = await createSite(tenantId, name);
      await load();
      setAsking(false);
      setDraftName('');
      /**
       * 必须说清"页面还是草稿"。新站点直接是 live 状态、域名也已生效，
       * 但三个默认页面都是 draft（和注册时一致：草稿不上公网）。
       * 不解释的话，客户点开地址看到 404，会以为站建坏了。
       */
      setNotice(
        `已创建「${name}」，地址是 ${created.host}。默认的三个页面还是草稿，去「页面内容」发布后访客才能看到。`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onResume = async (site: SiteContext): Promise<void> => {
    setBusy(site.site.id);
    setError(null);
    try {
      await resumeSite(tenantId, site.site.id);
      await load();
      setNotice(`「${site.site.name}」已恢复，前台可以正常访问了。`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  /**
   * 归档：前台 404，但名额收回来。
   *
   * 需要二次确认 —— 名字听起来像删除，虽然它其实不删任何东西。
   * 文案必须把"数据还在、随时能开回来"写清楚，否则客户会以为自己在删站。
   */
  const onArchive = async (site: SiteContext): Promise<void> => {
    const ok = await confirm({
      title: `归档「${site.site.name}」？`,
      message: (
        <>
          归档后这个站点前台打不开（404），但它<strong>不再占用套餐的站点名额</strong>，
          你可以用腾出来的名额建新站或恢复别的站。页面、商品、图片一条都不删，
          随时能从这里恢复回来。
        </>
      ),
      confirmText: '归档',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(site.site.id);
    setError(null);
    try {
      await archiveSite(tenantId, site.site.id);
      await load();
      setNotice(`「${site.site.name}」已归档，占用的名额已释放。`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  /**
   * 向导开着时，整页让给它。
   *
   * 列表和引导卡这时候都没有意义：零站点时列表本来就是空的，而引导卡
   * （"去发布页面""去绑域名"）说的是"你已经有站了该做什么"——
   * 向导还没走完的人看到它只会分心。
   *
   * 早返回放在所有 hook 之后，这是必须的：放在前面会让这一页的
   * hook 数量随 `wizardOpen` 变化，React 直接报错。
   */
  if (agentOpen) {
    return (
      <>
        {confirmDialog}
        <SiteAgentPanel
          tenantId={tenantId}
          onDone={() => void load()}
          onCancel={() => {
            setAgentOpen(false);
            void load();
          }}
          onNavigate={(key) => {
            setAgentOpen(false);
            onNavigate(key);
            void load();
          }}
        />
      </>
    );
  }

  if (wizardOpen) {
    return (
      <>
        {confirmDialog}
        <SiteWizard
          tenantId={tenantId}
          emailVerified={emailVerified}
          onDone={load}
          /*
           * 关闭时也重拉一次。
           *
           * 向导在第 3 步就把站建出来了 —— 客户可能在那之后直接点「返回我的站点」。
           * 不重拉的话，他回到的是一屏"还没有站点"，而库里的站已经在了，
           * 侧栏也还是收起的。刷新一下就把这个假象抹掉。
           */
          onCancel={() => {
            setWizardOpen(false);
            void load();
          }}
          /*
           * 完成屏把客户送去「域名」或「商品」。
           *
           * 先关向导再切页 —— 反过来的话，页面已经换了、向导还盖在上面，
           * 客户看到的是"点了没反应"。
           *
           * `load()` 照发：它同时上报 `onSitesChanged`，让侧栏从"只有我的站点"
           * 变成全菜单。切页之后这个组件会卸载，但上报落在 App 层，不会丢。
           */
          onNavigate={(key) => {
            setWizardOpen(false);
            onNavigate(key);
            void load();
          }}
          onDirtyChange={onWizardDirtyChange}
        />
      </>
    );
  }

  return (
    <>
      {confirmDialog}
      {/*
        引导卡放在列表上方而不是单独一页：客户注册后第一件事就是"我的站点"，
        清单要在他看到站点列表的同一屏里，否则等于藏起来了。
      */}
      <OnboardingCard tenantId={tenantId} emailVerified={emailVerified} onNavigate={onNavigate} />

      <div className="site-head">
        <div>
          <h2 className="site-head__title">我的站点</h2>
          <p className="muted site-head__sub">
            {usage.limit > 0
              ? `已用 ${usage.used} 个，套餐上限 ${usage.limit} 个`
              : `已用 ${usage.used} 个`}
          </p>
        </div>
        {/*
          没有 site:create 权限的人**看不到**这个按钮，而不是看到一个点了 403 的
          按钮：editor 与 viewer 不需要知道"这里本来可以建站"。

          零站点时也不画：那一刻屏幕中央本来就有一张「新建一个站点」的大卡片，
          右上角再挂一个同名入口，客户会以为两个不一样（一个带向导、一个不带）。
          这条按钮从今往后只服务"再开一个站"。
        */}
        {canCreate && sites.length > 0 ? (
          <Button variant="primary" onClick={() => setAsking(true)} disabled={atLimit}>
            新增站点
          </Button>
        ) : null}
      </div>

      {atLimit ? (
        <div className="notice notice--warn">
          当前套餐的 {usage.limit} 个站点名额已用满。升级套餐后可以继续添加。
        </div>
      ) : null}
      {notice ? <div className="notice notice--ok">{notice}</div> : null}
      {error ? <div className="notice notice--danger">{error}</div> : null}

      {/*
        竖版卡片网格。原来是通用 Card 一列拉满，三个站点长得一模一样；
        租户最多 3 个站，这里不追求"能排很多"，只要求"一眼分得清"。
      */}
      <section className="sitegrid">
        {loading ? <p className="muted">加载中…</p> : null}
        {sites.map((site, index) => {
          const suspended = site.site.status === 'suspended';
          const archived = site.site.status === 'archived';
          return (
            <SiteCard
              key={site.site.id}
              context={site}
              index={index + 1}
              busy={busy === site.site.id}
              resumeBlocked={resumeBlocked}
              hint={
                archived ? (
                  <>
                    已归档：前台 404，也不占套餐名额。内容、商品、图片都还在，
                    {resumeBlocked
                      ? `但当前套餐允许 ${usage.limit} 个站点同时在线（已有 ${liveCount} 个），要先腾出名额才能恢复。`
                      : '点「恢复」即可重新上线。'}
                  </>
                ) : suspended ? (
                  <>
                    套餐降档后站点数超出上限，这个站点被暂停：前台目前 404，内容与数据都还在。
                    {resumeBlocked
                      ? `当前套餐只允许 ${usage.limit} 个站点同时在线（已有 ${liveCount} 个）。可以归档别的站点腾出名额，或者升级套餐。`
                      : '现在就可以恢复，点「恢复站点」即可重新上线。'}
                  </>
                ) : null
              }
              onResume={() => void onResume(site)}
              onArchive={canArchive ? () => void onArchive(site) : undefined}
              onManageDomains={onManageDomains}
            />
          );
        })}
        {!loading && !error && sites.length === 0 ? (
          /**
           * 零站点空态。
           *
           * 注册不再自动建站之后，新客户进来看到的就是这一屏 —— 所以它不能
           * 是一句"还没有站点"。文案里那几句"不用域名、不用备案"是有用的：
           * 目标客户最常卡的两个顾虑就在这里。
           *
           * 「开始」进建站向导（站名 → 版式 → Logo）。多站点的「新增站点」
           * 按钮仍然走上面那个只填名字的弹窗：企业版客户加第二个站时，
           * 他要的是"再开一个"，不是再被引导一遍。
           */
          <div className="emptysite">
            <div className="emptysite__mark" aria-hidden="true" />
            <h3 className="emptysite__title">新建一个站点</h3>
            <p className="emptysite__lede">
              选个版式、填个名字，两分钟就有一个能打开的站。
              <br />
              不用域名，不用备案，也不用装任何东西。
            </p>
            {canCreate ? (
              atLimit ? (
                <p className="emptysite__note">
                  套餐的站点名额已经用完（{usage.used}/{usage.limit}）。归档一个不用的站，
                  或者升级套餐之后再建。
                </p>
              ) : (
                <>
                  <div className="emptysite__acts">
                    <Button variant="primary" onClick={() => setAgentOpen(true)}>
                      一句话建站
                    </Button>
                    <Button variant="ghost" onClick={() => setWizardOpen(true)}>
                      自己选版式
                    </Button>
                  </div>
                  {usage.limit > 0 ? (
                    <p className="emptysite__note">
                      套餐还剩 {Math.max(0, usage.limit - usage.used)} 个站点名额
                    </p>
                  ) : null}
                </>
              )
            ) : (
              <p className="emptysite__note">
                你的角色不能新建站点。需要的话请管理员在「成员管理」里调整权限。
              </p>
            )}
          </div>
        ) : null}
      </section>

      {/*
        命名弹窗。只有一个字段，所以不做成独立页面。
        描述里带上"地址会自动分配"：客户这时最想知道的是"我的域名填哪儿"，
        而答案是他不用填 —— 平台先给一个 icetus.net 的子域，自有域名去「域名绑定」页绑。
      */}
      <Modal
        open={asking}
        onClose={() => setAsking(false)}
        title="新增站点"
        description="给它起个内部名称（只有你和你同事看得到）。平台会自动分配一个 icetus.net 的子域，自有域名之后在「域名绑定」页绑定。"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAsking(false)} disabled={busy === 'create'}>
              取消
            </Button>
            <Button
              variant="primary"
              disabled={busy === 'create' || draftName.trim().length === 0}
              onClick={() => void onCreate()}
            >
              {busy === 'create' ? '创建中…' : '创建'}
            </Button>
          </>
        }
      >
        <label className="field-block">
          <span>站点名称</span>
          <input
            className="field"
            type="text"
            value={draftName}
            maxLength={60}
            autoFocus
            placeholder="例如：北美市场 / Aurora US"
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draftName.trim() && busy !== 'create') void onCreate();
            }}
          />
        </label>
        <p className="muted">
          新站点会带着三个默认页面（首页、产品、联系）起步，主题是基础版「极光」。
          页面默认是草稿，发布后访客才能看到。
        </p>
      </Modal>
    </>
  );
}

const STATUS_TONE: Record<string, 'success' | 'warn' | 'danger' | 'neutral'> = {
  active: 'success',
  verifying: 'warn',
  pending_dns: 'warn',
  releasing: 'warn',
  error: 'danger',
  suspended: 'danger',
  deleted: 'neutral',
};

function DomainsPage({ tenantId, tenantLabel }: { tenantId: string; tenantLabel: string }) {
  const [domains, setDomains] = useState<DomainView[]>([]);
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [host, setHost] = useState('');
  const [siteId, setSiteId] = useState('');
  const [notice, setNotice] = useState<{ tone: 'warn' | 'danger'; text: string } | null>(null);

  const reload = useCallback(async () => {
    const items = await fetchDomains(tenantId);
    setDomains(items);
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotice(null);
    Promise.all([fetchDomains(tenantId), fetchSites(tenantId)])
      .then(([domainItems, siteItems]) => {
        if (cancelled) return;
        setDomains(domainItems);
        setSites(siteItems);
        // 必须校验旧选择是否还在新列表里。原先写的是 `prev || 列表第一个`，
        // 切租户时 prev 是上一个租户的站点 ID（非空），于是永远不更新；
        // 而受控 select 的 value 不在 options 里时浏览器会显示第一项 ——
        // 看到的是「Aurora Lighting」，提交的却是 ningbo 的站点，报"站点不存在"。
        setSiteId((prev) =>
          siteItems.some((s) => s.site.id === prev) ? prev : (siteItems[0]?.site.id ?? ''),
        );
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // 只对"证书还在路上"的行轮询。客户关掉页面就停，所以后台定时任务同样必要。
  const verifyingIds = domains.filter((d) => d.status === 'verifying' && !d.ready).map((d) => d.id);
  const verifyingKey = verifyingIds.join(',');
  useEffect(() => {
    if (!verifyingKey) return;
    const ids = verifyingKey.split(',');
    const timer = setInterval(() => {
      void Promise.all(ids.map((id) => getDomain(tenantId, id).catch(() => null))).then(
        (updated) => {
          setDomains((prev) => prev.map((d) => updated.find((u) => u?.id === d.id) ?? d));
        },
      );
    }, 8000);
    return () => clearInterval(timer);
  }, [verifyingKey, tenantId]);

  const bind = async () => {
    const value = host.trim().toLowerCase();
    if (!value) return;
    // 兜底：选择项与列表不一致时先自救，别把注定失败的请求打到后端。
    // 后端只会回一句"站点不存在"，客户无从判断是该换站点还是刷新页面。
    if (!sites.some((s) => s.site.id === siteId)) {
      setNotice({ tone: 'danger', text: '站点选择已失效，请在右侧重新选择站点后再绑定。' });
      return;
    }
    setBusy('create');
    setNotice(null);
    try {
      const created = await createDomain(tenantId, siteId, value);
      setDomains((prev) => [created, ...prev]);
      setHost('');
    } catch (err) {
      setNotice({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const act = async (id: string, action: 'verify' | 'remove') => {
    setBusy(id);
    setNotice(null);
    try {
      const next =
        action === 'verify' ? await verifyDomain(tenantId, id) : await removeDomain(tenantId, id);
      setDomains((prev) => prev.map((d) => (d.id === id ? next : d)));
      if (action === 'remove') await reload();
    } catch (err) {
      setNotice({ tone: 'danger', text: err instanceof Error ? err.message : String(err) });
      await reload().catch(() => undefined);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Card
        title="绑定新域名"
        inline
        description={
          <>
            绑定到「{tenantLabel}」。裸域和 www 是两个主机名，各绑一次；建议两个都绑上，
            访客输哪个都能进。
            <span className="dot-sep">·</span>
            {/*
              唯一的"该怎么做"出口。这一步要客户离开后台去别人家的 DNS 面板里
              动手（腾讯云/阿里云/Cloudflare 的主机记录规则还相反），卡住的人
              远比输错域名的人多。链接与下面 DNS 卡片里的图文步骤是同一页。
            */}
            <a className="help-link" href={DNS_GUIDE_URL} target="_blank" rel="noreferrer">
              还不会操作？
            </a>
          </>
        }
      >
        <div className="form-row">
          <input
            className="field"
            placeholder="www.example.com"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void bind();
            }}
          />
          <select className="field" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            {sites.length === 0 ? (
              // 空下拉框会让人以为是页面坏了。给一句明确的占位，指向下一步该做什么。
              <option value="">{loading ? '加载站点中…' : '该租户下没有站点'}</option>
            ) : null}
            {sites.map((s) => (
              <option key={s.site.id} value={s.site.id}>
                {s.site.name}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            onClick={() => void bind()}
            disabled={busy === 'create' || !host.trim() || !siteId}
          >
            {busy === 'create' ? '提交中…' : '绑定'}
          </Button>
        </div>
        {notice ? <p className={notice.tone === 'warn' ? 'warn' : 'error'}>{notice.text}</p> : null}
        {!loading && !error && sites.length === 0 ? (
          <p className="warn">
            该租户下还没有站点，因此无法绑定域名。先到「我的站点」确认站点已创建。
          </p>
        ) : null}
      </Card>

      {loading ? <p className="muted">加载中…</p> : null}
      {error ? <p className="error">读取域名失败：{error}</p> : null}

      <section className="grid">
        {domains.map((d) => (
          <Card
            key={d.id}
            title={d.host}
            description={`证书：${d.certStatus}${d.certIssuedAt ? ` · 签发于 ${d.certIssuedAt.slice(0, 10)}` : ''}`}
            actions={
              <Badge tone={d.ready ? 'success' : (STATUS_TONE[d.status] ?? 'neutral')}>
                {d.ready ? '已上线' : d.status}
              </Badge>
            }
          >
            {d.message ? <p className="muted">{d.message.zh}</p> : null}

            {d.instructions ? (
              <div className="dns">
                {d.instructions.steps.map((step) => (
                  <div key={`${step.type}-${step.name}`} className="dns__row">
                    <span className="dns__type">{step.type}</span>
                    <code className="dns__name">{step.name}</code>
                    <code className="dns__value">{step.value}</code>
                    <Button
                      variant="ghost"
                      onClick={() => void navigator.clipboard?.writeText(step.value)}
                    >
                      复制
                    </Button>
                    {/*
                     * 说明跟着记录走，不再只渲染 steps[0]。
                     * 绑定根域时那句"根域 CNAME 需要服务商支持扁平化，不支持就改 www"
                     * 只写在 CNAME 那条的 note 里，渲染第一个的话客户永远看不到，
                     * 于是他会在阿里云后台找半天"根域怎么填 CNAME"。
                     */}
                    <p className="dns__note">{step.note.zh}</p>
                  </div>
                ))}
                {/*
                 * 这一步是整条流程里唯一要客户离开我们后台、去别人家界面里动手的地方，
                 * 也最容易卡住。只给文字note不够 —— 三家的「主机记录」规则是相反的
                 * （腾讯云/阿里云填前缀，Cloudflare 填完整域名），光说一句"填主机记录"
                 * 会让一半人填出一个 `...example.com.example.com`。
                 * 图文步骤里逐格标了该填哪一格，直接开新标签页给他，控制台不跳走。
                 */}
                <a className="dns__guide" href={DNS_GUIDE_URL} target="_blank" rel="noreferrer">
                  不会填？看腾讯云 / 阿里云 / Cloudflare 的图文步骤
                  <span aria-hidden="true"> →</span>
                </a>
              </div>
            ) : null}

            <div className="actions">
              {d.status !== 'deleted' && d.status !== 'active' ? (
                <Button
                  variant="primary"
                  onClick={() => void act(d.id, 'verify')}
                  disabled={busy === d.id}
                >
                  {busy === d.id ? '校验中…' : '我已添加，开始验证'}
                </Button>
              ) : null}
              {d.status !== 'deleted' ? (
                <Button
                  variant="ghost"
                  onClick={() => void act(d.id, 'remove')}
                  disabled={busy === d.id}
                >
                  解绑
                </Button>
              ) : null}
            </div>
          </Card>
        ))}
        {!loading && domains.length === 0 ? (
          <Card
            title="还没有绑定域名"
            description="填你想用的域名。裸域（example.com）和 www.example.com 都能绑，建议两个都绑上：访客输哪个都能进，搜索引擎只收录一份。"
          />
        ) : null}
      </section>
    </>
  );
}
